import { useState, useEffect, useRef, useCallback, useMemo } from "react";

// Throttle function to limit the frequency of resize updates
function throttle(
  func: (newWidth: number) => void,
  delay: number,
): (newWidth: number) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastExecTime = 0;

  return (newWidth: number) => {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func(newWidth);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => {
          func(newWidth);
          lastExecTime = Date.now();
        },
        delay - (currentTime - lastExecTime),
      );
    }
  };
}

export interface ContainerWidthBreakpoints {
  isExtraSmall: boolean; // < 400px
  isSmall: boolean; // 400px - 600px
  isMedium: boolean; // 600px - 800px
  isLarge: boolean; // 800px - 1000px
  isExtraLarge: boolean; // > 1000px
  width: number;
}

export function useContainerWidth(): [
  ContainerWidthBreakpoints,
  (node: HTMLDivElement | null) => void,
] {
  const [, setWidth] = useState(0);
  const [breakpoints, setBreakpoints] = useState<ContainerWidthBreakpoints>({
    isExtraSmall: true,
    isSmall: false,
    isMedium: false,
    isLarge: false,
    isExtraLarge: false,
    width: 0,
  });

  const resizeObserver = useRef<ResizeObserver | null>(null);

  const updateBreakpoints = useCallback((newWidth: number) => {
    setBreakpoints((prev) => {
      // Only update if breakpoints actually change to prevent unnecessary re-renders
      const newBreakpoints = {
        isExtraSmall: newWidth < 400,
        isSmall: newWidth >= 400 && newWidth < 600,
        isMedium: newWidth >= 600 && newWidth < 800,
        isLarge: newWidth >= 800 && newWidth < 1000,
        isExtraLarge: newWidth >= 1000,
        width: newWidth,
      };

      // Check if breakpoints actually changed
      if (
        prev.isExtraSmall === newBreakpoints.isExtraSmall &&
        prev.isSmall === newBreakpoints.isSmall &&
        prev.isMedium === newBreakpoints.isMedium &&
        prev.isLarge === newBreakpoints.isLarge &&
        prev.isExtraLarge === newBreakpoints.isExtraLarge &&
        Math.abs(prev.width - newBreakpoints.width) < 5 // Only update if width changes by more than 5px
      ) {
        return prev;
      }

      return newBreakpoints;
    });
  }, []);

  // Throttled version of updateBreakpoints
  const throttledUpdateBreakpoints = useMemo(() => {
    return throttle(updateBreakpoints, 16); // ~60fps
  }, [updateBreakpoints]);

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }

      if (node) {
        const updateWidth = () => {
          const newWidth = node.offsetWidth;
          setWidth(newWidth);
          throttledUpdateBreakpoints(newWidth);
        };

        updateWidth();

        resizeObserver.current = new ResizeObserver(() => {
          updateWidth();
        });

        resizeObserver.current.observe(node);
      }
    },
    [throttledUpdateBreakpoints],
  );

  useEffect(() => {
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  return [breakpoints, ref];
}
